/**
 * API Service
 * Centralized service for all API calls to the backend
 */

import { API_ENDPOINTS, API_CONFIG } from './config';
import type {
  Item,
  List,
  CreateItemDto,
  UpdateItemDto,
  CreateListDto,
  UpdateListDto,
  ApiError,
  HealthResponse,
} from './types';

/**
 * API Service Class
 * Handles all HTTP requests to the backend
 */
class ApiService {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: 'Une erreur est survenue',
        statusCode: response.status,
      }));
      throw new Error(error.message || `HTTP Error: ${response.status}`);
    }
    
    // Handle 204 No Content (empty response)
    if (response.status === 204) {
      return undefined as T;
    }
    
    return response.json();
  }

  private async request<T>(
    url: string,
    options?: RequestInit
  ): Promise<T> {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...API_CONFIG.headers,
          ...options?.headers,
        },
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Une erreur réseau est survenue');
    }
  }

  // ==================== LISTS ====================

  /**
   * Get all lists with their items
   */
  async getLists(): Promise<List[]> {
    return this.request<List[]>(API_ENDPOINTS.lists);
  }

  /**
   * Get a single list by ID
   */
  async getList(id: number): Promise<List> {
    return this.request<List>(API_ENDPOINTS.list(id));
  }

  /**
   * Create a new list
   */
  async createList(data: CreateListDto): Promise<List> {
    return this.request<List>(API_ENDPOINTS.lists, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a list
   */
  async updateList(id: number, data: UpdateListDto): Promise<List> {
    return this.request<List>(API_ENDPOINTS.list(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a list
   */
  async deleteList(id: number): Promise<void> {
    await this.request<void>(API_ENDPOINTS.list(id), {
      method: 'DELETE',
    });
  }

  // ==================== ITEMS ====================

  /**
   * Get all items
   */
  async getItems(): Promise<Item[]> {
    return this.request<Item[]>(API_ENDPOINTS.items);
  }

  /**
   * Create a new item
   */
  async createItem(data: CreateItemDto): Promise<Item> {
    return this.request<Item>(API_ENDPOINTS.items, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an item
   */
  async updateItem(id: number, data: UpdateItemDto): Promise<Item> {
    return this.request<Item>(API_ENDPOINTS.item(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete an item
   */
  async deleteItem(id: number): Promise<void> {
    await this.request<void>(API_ENDPOINTS.item(id), {
      method: 'DELETE',
    });
  }

  // ==================== HEALTH ====================

  /**
   * Check API health status
   */
  async checkHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>(API_ENDPOINTS.health);
  }
}

// Export singleton instance
export const apiService = new ApiService();
